import { useState, useEffect } from "react";
import { getAttachmentUrl } from "@utils/attachment.utils";

interface UseAttachmentReturn {
	url: string;
	isLoading: boolean;
	error: string | null;
}

// Cache to store attachment URLs with expiration
const attachmentCache = new Map<string, { url: string; expires: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Custom hook to fetch attachment URL by key with caching
 * @param key - The attachment key from backend
 * @returns Object containing url, isLoading, and error states
 */
export const useAttachment = (key?: string | null): UseAttachmentReturn => {
	const [url, setUrl] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!key) {
			setUrl("");
			setError(null);
			setIsLoading(false);
			return;
		}

		// Check cache first
		const cached = attachmentCache.get(key);
		const now = Date.now();

		if (cached && cached.expires > now) {
			// Use cached URL
			setUrl(cached.url);
			setIsLoading(false);
			setError(null);
			return;
		}

		const fetchUrl = async () => {
			try {
				setIsLoading(true);
				setError(null);

				const attachmentUrl = await getAttachmentUrl(key);

				// Cache the URL with expiration
				attachmentCache.set(key, {
					url: attachmentUrl,
					expires: now + CACHE_DURATION,
				});

				setUrl(attachmentUrl);
			} catch (err) {
				console.error("useAttachment hook - Failed to fetch attachment URL:", err);
				setError("Failed to load attachment");
				setUrl("");
			} finally {
				setIsLoading(false);
			}
		};

		fetchUrl();
	}, [key]);

	return { url, isLoading, error };
};
